

import { jitsiLocalStorage } from '@jitsi/js-utils';

import { COLOR_PICKER, CONFERENCE_SUBJECT, CONFERENCE_VIEW, FILM_STRIP_VIEW, POLLS_TAB_FOCUSED, SET_MEETING_LIMIT_EXCEEDED } from '../../conference/actionTypes';
import { LOCKED_LOCALLY, LOCKED_REMOTELY } from '../../room-lock';
import { LAYOUTS } from '../../video-layout';
import { ADD_FORCE_MUTE_REMOTE_PARTICIPANT,
    AUTH_STATUS_CHANGED,
    CALL_ENDED,
    CONFERENCE_EXPIRED,
    CONFERENCE_FAILED,
    CONFERENCE_JOINED,
    CONFERENCE_LEFT,
    CONFERENCE_SUBJECT_CHANGED,
    CONFERENCE_TIME,
    CONFERENCE_TIMESTAMP_CHANGED,
    CONFERENCE_WILL_JOIN,
    CONFERENCE_WILL_LEAVE,
    CONNECTION_INDICATOR_STATUS,
    EXTEND_CONFERENCE_EXPIRY,
    FORCE_MUTE_REMOTE_PARTICIPANT,
    FORCE_MUTE_REMOTE_PARTICIPANT_ID,
    GET_FORCE_MUTE_REMOTE_PARTICIPANT,
    IS_CUSTOM_PARTICIPANT_EVERYONE,
    IS_CUSTOM_PARTICIPANT_VISIBLE,
    LOADING_CHECKER,
    LOCK_STATE_CHANGED,
    MH_CONFERENCE_JOINED,
    MODERATOR_MUTED_US_ALL,
    MODERATOR_UNLOCK_AUDIO,
    MODERATOR_UNLOCK_VIDEO,
    NOTIFICATION_DETAILS,
    OPEN_NOTIFICATION,
    P2P_STATUS_CHANGED,
    PREJOIN_MOBILE_DISABLED,
    SET_FOLLOW_ME,
    SET_NAVIGATION_BAR_HIDDEN_ACTIONS,
    SET_OBJECT_FIT,
    SET_PASSWORD,
    SET_PENDING_SUBJECT_CHANGE,
    SET_ROOM,
    SET_START_MUTED_POLICY,
    SET_START_REACTIONS_MUTED,
    TOGGLE_LAYOUT_OVERLAY,
    TOGGLE_NAVIGATION_BAR_MORE_OPTIONS,
    TRANSPORT_STATE_CHANGED,
    USER_JOINED_TIME
} from '../conference/actionTypes';
import { CONNECTION_WILL_CONNECT, SET_LOCATION_URL } from '../connection';
import { MHConferenceErrors } from '../lib-meet-hour';
import { ReducerRegistry, assign, set } from '../redux';
import { ColorPalette } from '../styles';

import { REDUCE_ALL_USER_VOLUME_TEMPORARILY } from './constants';
import { isRoomValid } from './functions';
import logger from './logger';

const DEFAULT_STATE = {
    conference: undefined,
    e2eeSupported: undefined,
    joining: undefined,
    leaving: undefined,
    locked: undefined,
    membersOnly: undefined,
    password: undefined,
    passwordRequired: undefined,
    lockAudio: {},
    lockVideo: {},
    forcedIT: false,
    lockMyVideo: false,
    lockMyAudio: false,
    conferenceProps: undefined,
    mhConferenceJoined: false,
    moderatorunlockAudio: false,
    moderatorunlockVideo: false,
    isLoading: false,
    isConnecting: false,
    extendConferenceExpiry: false,
    conferenceEnded: false,
    conferenceEndedMessage: undefined,
    openNotification: false,
    isCustomParticipantVisible: false,
    isCustomParticipantEveryone: false,
    pollsTabFocused: false,
    filmStripView: LAYOUTS.TILE_VIEW,
    connectionIndicatorStatus: undefined,
    transportState: undefined,
    conferenceView: jitsiLocalStorage.getItem('conferenceView') || 'fixed',
    conferenceSubject: undefined,
    conferenceExpired: false,
    notifierDetails: {
        alertType: '',
        description: ''
    },
    objectFit: jitsiLocalStorage.getItem('objectFit') || 'cover',
    prejoinMobileDisabled: false,
    conferneceTime: '0:00',
    timeJoined: null,
    hosts: [],
    colorPicker: ColorPalette.tileBackground,
    tempMuted: false,
    navigationBarMoreOptions: false,
    navigationBarHiddenActions: [],
    isShowLayoutOverlay: false,
    isAudioMuteAll: false,
    isVideoMuteAll: false,
    meetingLimitExceeded: false,
    meetingLimitDetails: {}
};

/**
 * Listen for actions that contain the conference object, so that it can be
 * stored for use by other action creators.
 */
ReducerRegistry.register(
    'features/base/conference',
    (state = DEFAULT_STATE, action) => {
        switch (action.type) {
        case SET_MEETING_LIMIT_EXCEEDED:
            return {
                ...state,
                meetingLimitExceeded: action.isExceeded,
                meetingLimitDetails: action.details || {}
            };

        case AUTH_STATUS_CHANGED:
            return _authStatusChanged(state, action);

        case CONFERENCE_FAILED:
            return _conferenceFailed(state, action);

        case CONFERENCE_JOINED:
            return _conferenceJoined(state, action);

        case REDUCE_ALL_USER_VOLUME_TEMPORARILY:
            return {
                ...state,
                tempMuted: !state.tempMuted
            };

        case TOGGLE_NAVIGATION_BAR_MORE_OPTIONS:
            return {
                ...state,
                navigationBarMoreOptions: action.value
            };
        case SET_NAVIGATION_BAR_HIDDEN_ACTIONS:
            return {
                ...state,
                navigationBarHiddenActions: action.actions
            };
        case TOGGLE_LAYOUT_OVERLAY:
            return {
                ...state,
                isShowLayoutOverlay: action.value
            };

        case POLLS_TAB_FOCUSED:
            return {
                ...state,
                pollsTabFocused: action.value
            };

        case COLOR_PICKER:
            return {
                ...state,
                colorPicker: action.value
            };

        case FILM_STRIP_VIEW:
            return {
                ...state,
                filmStripView: action.value
            };

        case CONNECTION_INDICATOR_STATUS:
            if (state.connectionIndicatorStatus === action.status) {
                return state;
            }

            return {
                ...state,
                connectionIndicatorStatus: action.status
            };

        case TRANSPORT_STATE_CHANGED:
            return {
                ...state,
                transportState: action.transportState
            };

        case CONFERENCE_EXPIRED:
            return {
                ...state,
                conferenceExpired: true
            };

        case EXTEND_CONFERENCE_EXPIRY:
            return {
                ...state,
                extendConferenceExpiry: true
            };

        case SET_OBJECT_FIT:
            return {
                ...state,
                objectFit: action.value
            };

        case CALL_ENDED:
            return {
                ...state,
                conferenceEnded: action.conferenceEnded,
                conferenceEndedMessage: action.conferenceEndedMessage
            };

        case CONFERENCE_VIEW:
            return {
                ...state,
                conferenceView: action.value
            };

        case CONFERENCE_SUBJECT:
            return {
                ...state,
                conferenceSubject: action.value
            };

        case MODERATOR_UNLOCK_AUDIO:
            return {
                ...state,
                moderatorunlockAudio: true
            };

        case IS_CUSTOM_PARTICIPANT_VISIBLE:
            return {
                ...state,
                isCustomParticipantVisible: action.bool
            };

        case IS_CUSTOM_PARTICIPANT_EVERYONE:
            return {
                ...state,
                isCustomParticipantEveryone: action.bool
            };


        case MODERATOR_UNLOCK_VIDEO:
            return {
                ...state,
                moderatorunlockVideo: true
            };

        case MODERATOR_MUTED_US_ALL:
            if (action.mediaType === 'audio') {
                return {
                    ...state,
                    isAudioMuteAll: action.isMuteAll
                };
            } else if (action.mediaType === 'video') {
                return {
                    ...state,
                    isVideoMuteAll: action.isMuteAll
                };
            }

            return state;

        case CONFERENCE_SUBJECT_CHANGED:
            return set(state, 'subject', action.subject);

        case CONFERENCE_TIMESTAMP_CHANGED:
            return set(state, 'conferenceTimestamp', action.conferenceTimestamp);

        case CONFERENCE_LEFT:
        case CONFERENCE_WILL_LEAVE:
            return _conferenceLeftOrWillLeave(state, action);

        case CONFERENCE_WILL_JOIN:
            return _conferenceWillJoin(state, action);

        case CONNECTION_WILL_CONNECT:
            return set(state, 'authRequired', undefined);

        case FORCE_MUTE_REMOTE_PARTICIPANT:
            return _forceMute(state, action);

        case GET_FORCE_MUTE_REMOTE_PARTICIPANT:
            return _getMute(state, action);

        case FORCE_MUTE_REMOTE_PARTICIPANT_ID:
            return _moderatorForceMuted(state, action);

        case ADD_FORCE_MUTE_REMOTE_PARTICIPANT:
            return _addForceMuteParticipant(state, action);

        case LOCK_STATE_CHANGED:
            return _lockStateChanged(state, action);

        case P2P_STATUS_CHANGED:
            return _p2pStatusChanged(state, action);

        case SET_FOLLOW_ME:
            return set(state, 'followMeEnabled', action.enabled);

        case SET_START_REACTIONS_MUTED:
            return set(state, 'startReactionsMuted', action.muted);

        case SET_LOCATION_URL:
            return set(state, 'room', undefined);

        case SET_PASSWORD:
            return _setPassword(state, action);

        case SET_PENDING_SUBJECT_CHANGE:
            return set(state, 'pendingSubjectChange', action.subject);

        case SET_ROOM:
            return _setRoom(state, action);

        case SET_START_MUTED_POLICY:
            return {
                ...state,
                startAudioMutedPolicy: action.startAudioMutedPolicy,
                startVideoMutedPolicy: action.startVideoMutedPolicy
            };

        case MH_CONFERENCE_JOINED: {
            return {
                ...state,
                mhConferenceJoined: action.mhconferenceJoined
            };
        }

        case LOADING_CHECKER:
        {
            return {
                ...state,
                isLoading: action.isPrejoinLoading,
                isConnecting: action.isConnecting
            };
        }

        case OPEN_NOTIFICATION:
            return {
                ...state,
                openNotification: action.notication
            };

        case NOTIFICATION_DETAILS:
            return {
                ...state,
                notifierDetails: action.notifierDetails
            };

        case PREJOIN_MOBILE_DISABLED:
            return {
                ...state,
                prejoinMobileDisabled: action.prejoin
            };

        case CONFERENCE_TIME:
            return {
                ...state,
                conferneceTime: action.timer
            };

        case USER_JOINED_TIME:
            return {
                ...state,
                timeJoined: action.startTime

            };
        }

        return state;
    });

/**
 * Reduces a specific Redux action AUTH_STATUS_CHANGED of the feature
 * base/conference.
 *
 * @param {Object} state - The Redux state of the feature base/conference.
 * @param {Action} action - The Redux action AUTH_STATUS_CHANGED to reduce.
 * @private
 * @returns {Object} The new state of the feature base/conference after the
 * reduction of the specified action.
 */
function _authStatusChanged(state, { authEnabled, authLogin }) {
    return assign(state, {
        authEnabled,
        authLogin
    });
}

/**
 * Reduces a specific Redux action FORCE_MUTE_REMOTE_PARTICIPANT of the feature
 * base/conference.
 *
 * @param {Object} state - The Redux state of the feature base/conference.
 * @param {Object} action - The Redux action  to reduce.
 * @private
 * @returns {Object} The new state of the feature base/conference after the
 * reduction of the specified action.
 */
function _moderatorForceMuted(state, { mediaType, bool, isMuteAll, moderatorId }) {

    if (mediaType === 'video') {
        return {
            ...state,
            lockVideo: bool,
            isVideoMuteAll: isMuteAll === true || isMuteAll === 'true',
            videoForceMutedBy: moderatorId
        };
    } else if (mediaType === 'audio') {
        return {
            ...state,
            lockAudio: bool,
            isAudioMuteAll: isMuteAll === true || isMuteAll === 'true',
            audioForceMutedBy: moderatorId
        };
    }

    return state;
}

/**
 * Reduces a specific Redux action FORCE_MUTE_REMOTE_PARTICIPANT of the feature
 * base/conference.
 *
 * @param {Object} state - The Redux state of the feature base/conference.
 * @param {Object} action - The Redux action  to reduce.
 * @private
 * @returns {Object} The new state of the feature base/conference after the
 * reduction of the specified action.
 */
function _addForceMuteParticipant(state, { id, mediaType, bool }) {
    if (mediaType === 'video') {
        return {
            ...state,
            lockVideo: {
                ...state.lockVideo,
                [`${id}`]: bool
            },
            forcedIT: !state.forcedIT
        };
    } else if (mediaType === 'audio') {
        return {
            ...state,
            lockAudio: {
                ...state.lockAudio,
                [`${id}`]: bool
            },
            forcedIT: !state.forcedIT
        };
    }

    return state;
}

/**
 * Reduces a specific Redux action FORCE_MUTE_REMOTE_PARTICIPANT of the feature
 * base/conference.
 *
 * @param {Object} state - The Redux state of the feature base/conference.
 * @param {Object} action - The Redux action  to reduce.
 * @private
 * @returns {Object} The new state of the feature base/conference after the
 * reduction of the specified action.
 */
function _forceMute(state, { id, mediaType, bool }) {
    if (mediaType === 'video') {
        return {
            ...state,
            lockVideo: {
                ...state.lockVideo,
                [`${id}`]: bool
            },
            forcedIT: !state.forcedIT
        };
    } else if (mediaType === 'audio') {
        return {
            ...state,
            lockAudio: {
                ...state.lockAudio,
                [`${id}`]: bool
            },
            forcedIT: !state.forcedIT
        };
    }

    return state;
}


/**
 * Reduces a specific Redux action GET_FORCE_MUTE_REMOTE_PARTICIPANT of the feature
 * base/conference.
 *
 * @param {Object} state - The Redux state of the feature base/conference.
 * @param {Object} action - The Redux action  to reduce.
 * @private
 * @returns {Object} The new state of the feature base/conference after the
 * reduction of the specified action.
 */
function _getMute(state, { mediaType, bool }) {


    if (mediaType === 'video') {
        return {
            ...state,
            lockMyVideo: bool
        };

    } else if (mediaType === 'audio') {
        return {
            ...state,
            lockMyAudio: bool
        };
    }
}

/**
 * Reduces a specific Redux action CONFERENCE_FAILED of the feature
 * base/conference.
 *
 * @param {Object} state - The Redux state of the feature base/conference.
 * @param {Action} action - The Redux action CONFERENCE_FAILED to reduce.
 * @private
 * @returns {Object} The new state of the feature base/conference after the
 * reduction of the specified action.
 */
function _conferenceFailed(state, { conference, error }) {
    // The current (similar to getCurrentConference in
    // base/conference/functions.js) conference which is joining or joined:
    const conference_ = state.conference || state.joining;

    if (conference_ && conference_ !== conference) {
        return state;
    }

    let authRequired;
    let membersOnly;
    let passwordRequired;

    switch (error.name) {
    case MHConferenceErrors.AUTHENTICATION_REQUIRED:
        authRequired = conference;
        break;

    case MHConferenceErrors.CONFERENCE_ACCESS_DENIED:
    case MHConferenceErrors.MEMBERS_ONLY_ERROR:
        membersOnly = conference;
        break;

    case MHConferenceErrors.PASSWORD_REQUIRED:
        passwordRequired = conference;
        break;
    }

    return assign(state, {
        authRequired,
        conference: undefined,
        e2eeSupported: undefined,
        error,
        joining: undefined,
        leaving: undefined,

        /**
         * The indicator of how the conference/room is locked. If falsy, the
         * conference/room is unlocked; otherwise, it's either
         * {@code LOCKED_LOCALLY} or {@code LOCKED_REMOTELY}.
         *
         * @type {string}
         */
        locked: passwordRequired ? LOCKED_REMOTELY : undefined,
        membersOnly,
        password: undefined,

        /**
         * The MHConference instance which requires a password to join.
         *
         * @type {MHConference}
         */
        passwordRequired
    });
}

/**
 * Reduces a specific Redux action CONFERENCE_JOINED of the feature
 * base/conference.
 *
 * @param {Object} state - The Redux state of the feature base/conference.
 * @param {Action} action - The Redux action CONFERENCE_JOINED to reduce.
 * @private
 * @returns {Object} The new state of the feature base/conference after the
 * reduction of the specified action.
 */
function _conferenceJoined(state, { conference }) {
    // FIXME The indicator which determines whether a MHConference is locked
    // i.e. password-protected is private to lib-meet-hour. However, the
    // library does not fire LOCK_STATE_CHANGED upon joining a MHConference
    // with a password.
    // FIXME Technically MHConference.room is a private field.
    const locked = conference.room && conference.room.locked ? LOCKED_REMOTELY : undefined;

    // NOTE: isE2EESupported() performs runtime feature-detection (e.g. Insertable
    // Streams / RTCRtpScriptTransform). It must never be allowed to throw here:
    // this reducer runs synchronously inside the CONFERENCE_JOINED dispatch, and an
    // uncaught exception at this point aborts the whole dispatch, silently leaving
    // state.conference unset even though the conference was actually joined (media
    // keeps flowing since tracks were already attached in _conferenceWillJoin).
    let e2eeSupported;

    try {
        e2eeSupported = conference.isE2EESupported();
    } catch (error) {
        logger.error('conference.isE2EESupported() threw, defaulting to false:', error);
        e2eeSupported = false;
    }

    return assign(state, {
        authRequired: undefined,

        /**
         * The MHConference instance represented by the Redux state of the
         * feature base/conference.
         *
         * @type {MHConference}
         */
        conference,

        e2eeSupported,

        joining: undefined,
        membersOnly: undefined,
        leaving: undefined,

        /**
         * The indicator which determines whether the conference is locked.
         *
         * @type {boolean}
         */
        locked,
        passwordRequired: undefined
    });
}

/**
 * Reduces a specific redux action {@link CONFERENCE_LEFT} or
 * {@link CONFERENCE_WILL_LEAVE} for the feature base/conference.
 *
 * @param {Object} state - The redux state of the feature base/conference.
 * @param {Action} action - The redux action {@code CONFERENCE_LEFT} or
 * {@code CONFERENCE_WILL_LEAVE} to reduce.
 * @private
 * @returns {Object} The next/new state of the feature base/conference after the
 * reduction of the specified action.
 */
function _conferenceLeftOrWillLeave(state, { conference, type }) {
    const nextState = { ...state };

    // The redux action CONFERENCE_LEFT is the last time that we should be
    // hearing from a MHConference instance.
    //
    // The redux action CONFERENCE_WILL_LEAVE represents the order of the user
    // to leave a MHConference instance. From the user's perspective, there's
    // no going back (with respect to the instance itself). The app will perform
    // due clean-up like leaving the associated room, but the instance is no
    // longer the focus of the attention of the user and, consequently, the app.
    for (const p in state) {
        if (state[p] === conference) {
            nextState[p] = undefined;

            switch (p) {
            case 'conference':
            case 'passwordRequired':
                // XXX Clear/unset locked & password for a conference which has
                // been LOCKED_LOCALLY or LOCKED_REMOTELY.
                delete nextState.locked;
                delete nextState.password;
                break;
            }
        }
    }

    if (type === CONFERENCE_WILL_LEAVE) {
        // A CONFERENCE_WILL_LEAVE is of further consequence only if it is
        // expected i.e. if the specified conference is joining or joined.
        if (conference === state.joining || conference === state.conference) {
            /**
             * The MHConference instance which is currently in the process of
             * being left.
             *
             * @type {MHConference}
             */
            nextState.leaving = conference;
        }
    }

    nextState.meetingLimitExceeded = false;
    nextState.meetingLimitDetails = {};

    return nextState;
}

/**
 * Reduces a specific Redux action CONFERENCE_WILL_JOIN of the feature
 * base/conference.
 *
 * @param {Object} state - The Redux state of the feature base/conference.
 * @param {Action} action - The Redux action CONFERENCE_WILL_JOIN to reduce.
 * @private
 * @returns {Object} The new state of the feature base/conference after the
 * reduction of the specified action.
 */
function _conferenceWillJoin(state, { conference }) {
    return assign(state, {
        error: undefined,
        joining: conference
    });
}

/**
 * Reduces a specific Redux action LOCK_STATE_CHANGED of the feature
 * base/conference.
 *
 * @param {Object} state - The Redux state of the feature base/conference.
 * @param {Action} action - The Redux action LOCK_STATE_CHANGED to reduce.
 * @private
 * @returns {Object} The new state of the feature base/conference after the
 * reduction of the specified action.
 */
function _lockStateChanged(state, { conference, locked }) {
    if (state.conference !== conference) {
        return state;
    }

    return assign(state, {
        locked: locked ? state.locked || LOCKED_REMOTELY : undefined,
        password: locked ? state.password : undefined
    });
}

/**
 * Reduces a specific Redux action P2P_STATUS_CHANGED of the feature
 * base/conference.
 *
 * @param {Object} state - The Redux state of the feature base/conference.
 * @param {Action} action - The Redux action P2P_STATUS_CHANGED to reduce.
 * @private
 * @returns {Object} The new state of the feature base/conference after the
 * reduction of the specified action.
 */
function _p2pStatusChanged(state, action) {
    return set(state, 'p2p', action.p2p);
}

/**
 * Reduces a specific Redux action SET_PASSWORD of the feature base/conference.
 *
 * @param {Object} state - The Redux state of the feature base/conference.
 * @param {Action} action - The Redux action SET_PASSWORD to reduce.
 * @private
 * @returns {Object} The new state of the feature base/conference after the
 * reduction of the specified action.
 */
function _setPassword(state, { conference, method, password }) {
    switch (method) {
    case conference.join:
        return assign(state, {
            // 1. The MHConference which transitions away from
            // passwordRequired MUST remain in the redux state
            // features/base/conference until it transitions into
            // conference; otherwise, there is a span of time during which
            // the redux state does not even know that there is a
            // MHConference whatsoever.
            //
            // 2. The redux action setPassword will attempt to join the
            // MHConference so joining is an appropriate transitional
            // redux state.
            //
            // 3. The redux action setPassword will perform the same check
            // before it proceeds with the re-join.
            joining: state.conference ? state.joining : conference,
            locked: LOCKED_REMOTELY,

            /**
                 * The password with which the conference is to be joined.
                 *
                 * @type {string}
                 */
            password
        });

    case conference.lock:
        return assign(state, {
            locked: password ? LOCKED_LOCALLY : undefined,
            password
        });
    }

    return state;
}

/**
 * Reduces a specific Redux action SET_ROOM of the feature base/conference.
 *
 * @param {Object} state - The Redux state of the feature base/conference.
 * @param {Action} action - The Redux action SET_ROOM to reduce.
 * @private
 * @returns {Object} The new state of the feature base/conference after the
 * reduction of the specified action.
 */
function _setRoom(state, action) {
    let { room } = action;

    if (!isRoomValid(room)) {
        // Technically, there are multiple values which don't represent valid
        // room names. Practically, each of them is as bad as the rest of them
        // because we can't use any of them to join a conference.
        room = undefined;
    }

    /**
     * The name of the room of the conference (to be) joined.
     *
     * @type {string}
     */
    return assign(state, {
        error: undefined,
        room
    });
}

